import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
} from 'aws-amplify/auth';

import { environment } from '../../environments/environment';
import { SignoutService } from './signout.service';

import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
  GetIdCommandInput,
  GetCredentialsForIdentityCommandInput,
} from '@aws-sdk/client-cognito-identity';

import {
  CognitoIdentityProviderClient,
  AdminDisableUserCommandInput,
  AdminEnableUserCommand,
  AdminEnableUserCommandInput,
  AdminDisableUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';

export interface IUser {
  email: string;
  givenName: string;
  lastName: string;
  zoneInfo: string;
  phoneNumber: string;
  userType: string;
  address: string;
  password: string;
  showPassword: boolean;
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private authenticationSubject: BehaviorSubject<any>;

  // SRA003-271: in-flight + post-resolve cache of the Cognito GetUser
  // (fetchUserAttributes) call. Shared across concurrent callers within a
  // session; invalidated on signIn / signOut / updateUser.
  private userPromise: Promise<any> | null = null;

  constructor(private signoutApi: SignoutService) {
    if (!(environment as any).localMode) {
      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: environment.cognito.userPoolId,
            userPoolClientId: environment.cognito.userPoolWebClientId,
            identityPoolId: `${environment.aws.region}:${environment.aws.identityPoolId}`,
          },
        },
      });
    }

    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  public signUp(user: IUser): Promise<any> {
    let phoneNumber = user.phoneNumber;

    if (!phoneNumber.startsWith('+61') && !phoneNumber.startsWith('61')) {
      phoneNumber = '+61' + phoneNumber;
    }
    return signUp({
      username: user.email,
      password: user.password,
      options: {
        userAttributes: {
          given_name: user.givenName,
          family_name: user.lastName,
          zoneinfo: 'AU',
          address: user.address,
          phone_number: phoneNumber,
          'custom:user_type': user.userType,
          'custom:accepted_disclaimer': 'false',
        },
      },
    });
  }

  public resendSignUp(user: IUser): Promise<any> {
    return resendSignUpCode({ username: user.email });
  }

  public confirmSignUp(user: IUser, otp: any): Promise<any> {
    return confirmSignUp({
      username: user.email,
      confirmationCode: otp,
    });
  }

  //send the reset psw code to a specific user
  public forgotPassword(user: IUser): Promise<any> {
    return resetPassword({ username: user.email });
  }

  //reset psw for that user using the code
  public forgotPasswordSubmit(
    user: IUser,
    otp: any,
    newpassword: any
  ): Promise<any> {
    return confirmResetPassword({
      username: user.email,
      confirmationCode: otp,
      newPassword: newpassword,
    });
  }

  public async signIn(user: IUser): Promise<any> {
    this.userPromise = null;
    if ((environment as any).localMode) {
      const res = await fetch(`${(environment as any).localGatewayUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, password: user.password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Invalid email or password');
      }
      const data = await res.json();
      const now = new Date();
      localStorage.setItem(environment.keys.accessToken, data.access_token);
      localStorage.setItem(environment.keys.userType, data.user_type);
      localStorage.setItem(environment.keys.date, now.toLocaleDateString());
      this.authenticationSubject.next(true);
      return data;
    }

    // v6 throws "There is already a signed in user" if any session exists
    // (cookies / localStorage / partially-failed prior signIn). v4 silently
    // replaced — preserve that UX by signing the prior session out first.
    await signOut().catch(() => {});

    const { isSignedIn } = await signIn({
      username: user.email,
      password: user.password,
    });
    if (!isSignedIn) {
      throw new Error('Sign-in did not complete');
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!idToken || !accessToken) {
      throw new Error('No tokens after signIn');
    }

    const attrs = (await fetchUserAttributes()) as Record<string, string>;
    const userPoolId = Amplify.getConfig().Auth?.Cognito?.userPoolId;
    if (!userPoolId) {
      throw new Error('Amplify not configured');
    }

    const now = new Date();
    localStorage.setItem(
      environment.keys.currentUser,
      JSON.stringify({ attributes: attrs })
    );
    localStorage.setItem(
      environment.keys.userType,
      attrs['custom:user_type'] ?? ''
    );
    localStorage.setItem(environment.keys.date, now.toLocaleDateString());
    localStorage.setItem(environment.keys.accessToken, accessToken);

    const client = new CognitoIdentityClient({
      region: environment.aws.region,
    });

    const paramsId = {} as GetIdCommandInput;
    paramsId.IdentityPoolId =
      environment.aws.region + ':' + environment.aws.identityPoolId;
    paramsId.Logins = {
      [`cognito-idp.${environment.aws.region}.amazonaws.com/${userPoolId}`]:
        idToken,
    };
    const dataPoolId = await client.send(new GetIdCommand(paramsId));

    const params = {} as GetCredentialsForIdentityCommandInput;
    params.IdentityId = dataPoolId.IdentityId;
    params.Logins = {
      [`cognito-idp.${environment.aws.region}.amazonaws.com/${userPoolId}`]:
        idToken,
    };
    const data = await client.send(new GetCredentialsForIdentityCommand(params));

    localStorage.setItem(
      environment.keys.accessKey,
      data.Credentials?.AccessKeyId || ''
    );
    localStorage.setItem(
      environment.keys.secretKey,
      data.Credentials?.SecretKey || ''
    );
    localStorage.setItem(
      environment.keys.sessionToken,
      data.Credentials?.SessionToken || ''
    );

    this.authenticationSubject.next(true);
  }

  public disableUser(username: string): Promise<any> {
    var client = new CognitoIdentityProviderClient({
      region: environment.aws.region,
      credentials: {
        accessKeyId: localStorage.getItem(environment.keys.accessKey) || '',
        secretAccessKey: localStorage.getItem(environment.keys.secretKey) || '',
        sessionToken: localStorage.getItem(environment.keys.sessionToken) || '',
      },
    });
    const params = {} as AdminDisableUserCommandInput;
    params.UserPoolId = environment.cognito.userPoolId;
    params.Username = username;

    const command = new AdminDisableUserCommand(params);

    return client.send(command);
  }

  public enableUser(username: string): Promise<any> {
    var client = new CognitoIdentityProviderClient({
      region: environment.aws.region,
      credentials: {
        accessKeyId: localStorage.getItem(environment.keys.accessKey) || '',
        secretAccessKey: localStorage.getItem(environment.keys.secretKey) || '',
        sessionToken: localStorage.getItem(environment.keys.sessionToken) || '',
      },
    });
    const params = {} as AdminEnableUserCommandInput;
    params.UserPoolId = environment.cognito.userPoolId;
    params.Username = username;

    const command = new AdminEnableUserCommand(params);

    return client.send(command);
  }

  public signOut(): Promise<any> {
    if ((environment as any).localMode) {
      this.authenticationSubject.next(false);
      localStorage.removeItem(environment.keys.accessToken);
      localStorage.removeItem(environment.keys.date);
      this.clearUserData();
      return Promise.resolve();
    }
    // SRA003-208: invoke BE GlobalSignOut while the access token is still
    // valid (before Amplify signOut clears it). Failure is non-blocking —
    // FE sign-out must still complete even if the BE call errors.
    return firstValueFrom(this.signoutApi.invoke())
      .catch(() => undefined)
      .then(() => signOut())
      .then(() => {
        this.authenticationSubject.next(false);
      })
      .then(() => {
        localStorage.removeItem(environment.keys.accessToken);
        localStorage.removeItem(environment.keys.date);
        this.clearUserData();
      });
  }

  public async isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return true;
    }
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch {
      return false;
    }
  }

  public getUser(): Promise<any> {
    if (!this.userPromise) {
      this.userPromise = this.fetchUser().catch((err) => {
        // Don't cache failures — next call retries.
        this.userPromise = null;
        throw err;
      });
    }
    return this.userPromise;
  }

  private async fetchUser(): Promise<any> {
    if ((environment as any).localMode) {
      const token = localStorage.getItem(environment.keys.accessToken);
      if (!token) return null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          username: payload.sub,
          attributes: {
            email: payload.email,
            given_name: payload.given_name || '',
            family_name: payload.family_name || '',
            'custom:user_type': (payload['custom:user_type'] || '').toUpperCase(),
          },
        };
      } catch {
        return null;
      }
    }
    const attrs = (await fetchUserAttributes()) as Record<string, string>;
    return { attributes: attrs };
  }

  public async updateUser(user: any): Promise<void> {
    // Accept either { attributes: {...} } (contractor-home / grower-home) or
    // a flat attribute object (legacy home.component path) to preserve the
    // v4 caller surface without forcing component-level changes.
    const source: Record<string, string> = user?.attributes ?? user ?? {};
    const attrs = { ...source };
    // Strip attributes Cognito rejects via UpdateUserAttributes:
    //   sub, identities          — read-only system attributes
    //   email_verified, phone_number_verified — require VerifyUserAttribute flow
    //   custom:user_type         — pool-defined immutable
    for (const k of ['sub', 'email_verified', 'phone_number_verified', 'identities', 'custom:user_type']) {
      delete attrs[k];
    }
    await updateUserAttributes({ userAttributes: attrs });
    this.userPromise = null;
  }

  clearUserData(): void {
    this.userPromise = null;
    localStorage.removeItem(environment.keys.accessKey);
    localStorage.removeItem(environment.keys.secretKey);
    localStorage.removeItem(environment.keys.sessionToken);
    localStorage.removeItem(environment.keys.currentUser);
    localStorage.removeItem(environment.keys.userType);
  }
}
